import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { ClipboardText, User, CalendarBlank } from "@phosphor-icons/react";

const STATUS_LABEL = { ok: "OK", mangel: "Mangel", entfaellt: "Entfällt", nicht_zugaenglich: "Nicht zugänglich", nicht_geprueft: "Nicht geprüft" };
const fmt = (iso) => { try { return new Date(iso).toLocaleString("de-DE", { dateStyle: "medium", timeStyle: "short" }); } catch { return iso; } };

export function InspectionsDialog({ component, open, onOpenChange }) {
  const [list, setList] = useState(null);

  useEffect(() => {
    if (!open || !component?.id) return;
    setList(null);
    api.get(`/v1/components/${component.id}/inspections`).then((r) => setList(r.data || [])).catch(() => setList([]));
  }, [open, component]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="inspections-dialog" className="max-h-[90vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-heading"><ClipboardText size={18} weight="duotone" className="text-primary" /> Bestandsprüfungen</DialogTitle>
          <DialogDescription>{component?.bezeichnung} — dokumentierte Ist-Zustands-Prüfungen (Erstaufnahme).</DialogDescription>
        </DialogHeader>

        {list === null ? <div className="py-8 text-center text-sm text-muted-foreground">Lädt …</div>
          : list.length === 0 ? <div className="py-8 text-center text-sm text-muted-foreground" data-testid="inspections-empty">Für diese Komponente wurde noch keine Bestandsprüfung gespeichert.</div>
            : (
              <div className="space-y-3">
                {list.map((ins) => {
                  const c = ins.counts || {};
                  const maengel = (ins.results || []).filter((r) => r.status === "mangel");
                  return (
                    <div key={ins.id} className="rounded-md border border-border p-3" data-testid={`inspection-${ins.id}`}>
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <div className="flex items-center gap-3 text-xs text-muted-foreground">
                          <span className="flex items-center gap-1"><CalendarBlank size={13} /> {fmt(ins.created_at)}</span>
                          <span className="flex items-center gap-1"><User size={13} /> {ins.created_by}</span>
                        </div>
                        <div className="flex flex-wrap gap-1.5">
                          {c.ok > 0 && <Badge variant="outline" className="border-emerald-500/50 bg-emerald-500/10 text-[10px] text-emerald-700">OK: {c.ok}</Badge>}
                          {c.mangel > 0 && <Badge variant="outline" className="border-red-500/50 bg-red-500/10 text-[10px] text-red-700">Mangel: {c.mangel}</Badge>}
                          {c.nicht_geprueft > 0 && <Badge variant="outline" className="text-[10px]">Nicht geprüft: {c.nicht_geprueft}</Badge>}
                          <Badge variant="outline" className="text-[10px]">gesamt {c.total || (ins.results || []).length}</Badge>
                        </div>
                      </div>
                      {ins.note && <p className="mt-2 text-sm">{ins.note}</p>}
                      {maengel.length > 0 && (
                        <ul className="mt-2 space-y-1 border-t border-border pt-2 text-sm">
                          {maengel.map((m, i) => (
                            <li key={i} className="flex items-start gap-1.5" data-testid={`inspection-mangel-${i}`}>
                              <Badge variant="outline" className="mt-0.5 shrink-0 border-red-500/50 bg-red-500/10 text-[9px] text-red-700">{STATUS_LABEL[m.status]}</Badge>
                              <span><b>{m.beschreibung}</b>{m.bemerkung ? <span className="text-muted-foreground"> — {m.bemerkung}</span> : null}</span>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
      </DialogContent>
    </Dialog>
  );
}
